// Author: Sunil Jain
// Timestamp: 2025-04-30 03:15:00 PM EDT

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  TextField,
  Button,
  Grid,
  Link as MuiLink,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import logger from '../utils/logger';
import authFetch from '../utils/authFetch'; 

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
// Correct backend endpoint for confirming the reset
const RESET_PASSWORD_ENDPOINT = '/auth/reset-password';

function ResetPassword() {
  const { token } = useParams(); // Get token from URL parameter
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing password reset token link.');
      logger.error("ResetPassword: No token found in URL parameters.");
    }
     logger.debug("ResetPassword page rendered for token:", token ? `${token.substring(0, 8)}...` : 'NONE');
  }, [token]);

  // Toggle password visibility handlers
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);
  const handleMouseDownPassword = (event) => { event.preventDefault(); };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!password || !confirmPassword) { setError('Please enter and confirm your new password.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters long.'); return; }
    if (!token) { setError('Cannot reset password. The reset link is invalid or incomplete.'); return; }

    setLoading(true);
    logger.debug(`Attempting password reset confirmation for token: ${token.substring(0, 8)}...`);

    const payload = {
      token: token,
      new_password: password
    };

    try {
      // Use the centralized authFetch utility
      const data = await authFetch(RESET_PASSWORD_ENDPOINT, {
        method: 'POST',
        body: payload,
      });

      logger.info("Password reset successful:", data?.message);
      setSuccess(data?.message || 'Password reset successful! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (err) {
      logger.error("Password reset failed:", err);
      // authFetch automatically provides a clean error message
      setError(err.message || 'Password reset failed. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
       <Paper elevation={3} sx={{ marginTop: 8, padding: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', }} >
          <Typography component="h1" variant="h5" gutterBottom> Reset Your Password </Typography>
          {error && ( <Alert severity="error" sx={{ width: '100%', mt: 2, mb: 1 }}> {error} </Alert> )}
          {success && ( <Alert severity="success" sx={{ width: '100%', mt: 2, mb: 1 }}> {success} </Alert> )}

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
            {/* New Password Field */}
            <TextField margin="normal" required fullWidth name="password" label="New Password" type={showPassword ? 'text' : 'password'} id="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading || !!success} autoFocus helperText="Minimum 8 characters" InputProps={{ endAdornment: ( <InputAdornment position="end"> <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end"> {showPassword ? <VisibilityOff /> : <Visibility />} </IconButton> </InputAdornment> ), }} />
            {/* Confirm New Password Field */}
            <TextField margin="normal" required fullWidth name="confirmPassword" label="Confirm New Password" type={showConfirmPassword ? 'text' : 'password'} id="confirmPassword" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} disabled={loading || !!success} InputProps={{ endAdornment: ( <InputAdornment position="end"> <IconButton aria-label="toggle confirm password visibility" onClick={handleClickShowConfirmPassword} onMouseDown={handleMouseDownPassword} edge="end"> {showConfirmPassword ? <VisibilityOff /> : <Visibility />} </IconButton> </InputAdornment> ), }} />
            <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} disabled={loading || !!success || !token} >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
            </Button>
            <Grid container justifyContent="flex-end">
              <Grid item>
                <MuiLink component={RouterLink} to="/login" variant="body2"> Back to Sign In </MuiLink>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}

export default ResetPassword;